import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageTransition from "../components/PageTransition";
import axios from "axios";
import toast from "react-hot-toast";
import { BASE_ENDPOINT } from "../helpers/Config";
import { useAuth } from "../context/AuthContext";
import { AnimatePresence, motion } from "framer-motion";
import {
  FaUserPlus,
  FaSyncAlt,
  FaTrash,
  FaUsers,
  FaCheckCircle,
  FaTimesCircle,
  FaCrown,
  FaTimes,
  FaInfoCircle,
  FaExternalLinkAlt,
} from "react-icons/fa";

const DEFAULT_DAYS = 3;
const DEFAULT_MAX_DAYS = 7;

function n(v) {
  const num = Number(v);
  return Number.isFinite(num) ? num : 0;
}

function nfmt(v, digits = 0) {
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n(v));
}

function money(v) {
  return nfmt(v, 2);
}

function safeText(v) {
  return v === null || v === undefined || v === "" ? "—" : String(v);
}

function formatDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return safeText(d);
  return dt.toLocaleString("en-IN");
}

function formatDay(d) {
  if (!d) return "—";
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return safeText(d);
}

function clampDays(value, maxDays = DEFAULT_MAX_DAYS) {
  const num = Number(value);
  if (!Number.isFinite(num) || num < 1) return DEFAULT_DAYS;
  return Math.min(Math.floor(num), maxDays);
}

function asDayList(value) {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return [value];
  return [];
}

function handleApiError(errorPayload, logout) {
  const msg =
    typeof errorPayload === "string"
      ? errorPayload
      : errorPayload?.message || "Request failed";
  if (String(msg).toLowerCase().includes("session expired")) {
    toast.error(msg);
    logout();
    return;
  }
  toast.error(msg);
}

function StatusBadge({ active }) {
  return active ? (
    <span
      className="badge bg-success d-inline-flex align-items-center justify-content-center"
      title="Active"
      style={{ width: 26, height: 26, padding: 0 }}
    >
      <FaCheckCircle />
    </span>
  ) : (
    <span
      className="badge bg-danger d-inline-flex align-items-center justify-content-center"
      title="Inactive"
      style={{ width: 26, height: 26, padding: 0 }}
    >
      <FaTimesCircle />
    </span>
  );
}

function profitTone(v) {
  const num = n(v);
  if (num > 0) return "text-success";
  if (num < 0) return "text-danger";
  return "";
}

function LevelDepositTable({ byLevel }) {
  const levels = [1, 2, 3, 4, 5, 6];
  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle mb-0">
        <thead>
          <tr>
            <th>Level</th>
            <th className="text-end">First</th>
            <th className="text-end">Normal</th>
            <th className="text-end">Total</th>
          </tr>
        </thead>
        <tbody>
          {levels.map((lvl) => {
            const block = byLevel?.[`level_${lvl}`] || {};
            const first = block.first || {};
            const normal = block.normal || {};
            const totalAmt = n(first.amount) + n(normal.amount);
            const totalCnt = n(first.count) + n(normal.count);
            return (
              <tr key={`lvl-${lvl}`}>
                <td className="fw-semibold">L{lvl}</td>
                <td className="text-end">
                  {money(first.amount)}
                  <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                    {nfmt(first.count)}
                  </div>
                </td>
                <td className="text-end">
                  {money(normal.amount)}
                  <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                    {nfmt(normal.count)}
                  </div>
                </td>
                <td className="text-end">
                  {money(totalAmt)}
                  <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                    {nfmt(totalCnt)}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function DayCard({ day }) {
  const deposits = day?.deposits || {};
  const withdrawals = day?.withdrawals || {};
  const profit = day?.trades?.profit;
  const byLevel = day?.deposits_by_level || {};

  return (
    <div className="card border-0 h-100">
      <div className="card-header d-flex align-items-center justify-content-between">
        <span className="fw-semibold">{formatDay(day?.date)}</span>
        <span className="badge text-bg-secondary">{safeText(day?.date)}</span>
      </div>
      <div className="card-body">
        <div className="row g-2 mb-3">
          <div className="col-12">
            <div className="border rounded p-2" style={{ borderColor: "var(--tb-line)", background: "var(--tb-bg-soft)" }}>
              <div className="text-muted small">Deposits</div>
              <div className="fw-semibold">{money(deposits.amount)}</div>
              <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                {nfmt(deposits.count)} txns · includes exchange buy USDT
              </div>
            </div>
          </div>
          <div className="col-6">
            <div className="border rounded p-2 h-100" style={{ borderColor: "var(--tb-line)", background: "var(--tb-bg-soft)" }}>
              <div className="text-muted small">Withdrawals</div>
              <div className="fw-semibold">{money(withdrawals.amount)}</div>
              <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                {nfmt(withdrawals.count)} · incl. ex. sell
              </div>
            </div>
          </div>
          <div className="col-6">
            <div className="border rounded p-2 h-100" style={{ borderColor: "var(--tb-line)", background: "var(--tb-bg-soft)" }}>
              <div className="text-muted small">Trade profit</div>
              <div className={`fw-semibold ${profitTone(profit)}`}>{money(profit)}</div>
              <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                win − loss
              </div>
            </div>
          </div>
        </div>

        <div className="small fw-semibold mb-2">Fund deposits by level</div>
        <LevelDepositTable byLevel={byLevel} />
      </div>
    </div>
  );
}

function DayBusinessSection({ title, icon, rows }) {
  const list = asDayList(rows);

  return (
    <div className="mb-2">
      <div className="d-flex align-items-center justify-content-between gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <span className="tb-panel-icon">{icon}</span>
          <span className="fw-semibold">{title}</span>
        </div>
        <span className="badge text-bg-secondary">{nfmt(list.length)} days</span>
      </div>

      {list.length === 0 ? (
        <div className="text-muted small">No day-wise data</div>
      ) : (
        <div className="row g-3">
          {list.map((day, idx) => (
            <div className="col-12 col-lg-6 col-xxl-4" key={day?.date || `day-${idx}`}>
              <DayCard day={day} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DetailsModal({ open, item, onClose, onRemove, removing }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const user = item?.user || {};
  const business = item?.business || {};
  const username = user.username;
  const mobile =
    user.mobile == null || user.mobile === ""
      ? "—"
      : `+${safeText(user.country_code)} ${safeText(user.mobile)}`;

  return (
    <AnimatePresence>
      {open && item ? (
        <>
          <motion.div
            className="position-fixed top-0 start-0 w-100 h-100"
            style={{ background: "var(--tb-overlay)", zIndex: 1080 }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          <motion.div
            className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column"
            style={{
              zIndex: 1090,
              background: "var(--tb-bg)",
            }}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.2 }}
            role="dialog"
            aria-modal="true"
          >
            {/* Sticky header */}
            <div
              className="border-bottom px-3 px-md-4 py-3 d-flex align-items-start justify-content-between gap-3"
              style={{ background: "var(--tb-bg-elevated)", borderColor: "var(--tb-line)" }}
            >
              <div className="d-flex align-items-start gap-3 min-w-0">
                <span className="tb-panel-icon flex-shrink-0" style={{ width: 40, height: 40, fontSize: "1rem" }}>
                  <FaCrown />
                </span>
                <div className="min-w-0">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <h2 className="h5 mb-0 tb-display text-truncate">{safeText(user.name)}</h2>
                    <StatusBadge active={!!user.status} />
                    {user.is_demo ? <span className="badge text-bg-secondary">Demo</span> : null}
                  </div>
                  <div className="text-muted small mt-1">
                    ID <span className="fw-semibold text-primary">{safeText(user.user_id)}</span>
                    {" · "}
                    {mobile}
                  </div>
                  <div className="text-muted small mt-1">
                    Window {safeText(business.from_date)} → {safeText(business.to_date)} ({nfmt(business.days)} days)
                  </div>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 flex-shrink-0">
                {username ? (
                  <Link className="btn btn-outline-secondary btn-sm d-none d-sm-inline-flex align-items-center gap-1" to={`/user-profile/${username}`}>
                    <FaExternalLinkAlt /> Profile
                  </Link>
                ) : null}
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm d-inline-flex align-items-center gap-1"
                  disabled={removing}
                  onClick={() => onRemove(user.user_id)}
                >
                  <FaTrash /> Remove
                </button>
                <button type="button" className="tb-icon-btn" onClick={onClose} aria-label="Close">
                  <FaTimes />
                </button>
              </div>
            </div>

            {/* Scrollable body */}
            <div className="flex-grow-1 overflow-auto p-3 p-md-4">
              <div className="row g-3 mb-3">
                <div className="col-6 col-md-3">
                  <div className="card border-0 h-100">
                    <div className="card-body py-3">
                      <div className="text-muted small">Team</div>
                      <div className="h5 mb-0 tb-display">{nfmt(user.team_count)}</div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card border-0 h-100">
                    <div className="card-body py-3">
                      <div className="text-muted small">Coin / Lock</div>
                      <div className="h6 mb-0">
                        {money(user.balance?.coin?.total)} / {money(user.balance?.coin?.lock)}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card border-0 h-100">
                    <div className="card-body py-3">
                      <div className="text-muted small">USDT</div>
                      <div className="h6 mb-0">{money(user.balance?.usdt)}</div>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card border-0 h-100">
                    <div className="card-body py-3">
                      <div className="text-muted small">Added</div>
                      <div className="small fw-semibold mb-0">{formatDate(user.added_date)}</div>
                    </div>
                  </div>
                </div>
              </div>

              {user.remark ? (
                <div className="alert alert-light border mb-3 py-2" style={{ background: "var(--tb-bg-soft)", borderColor: "var(--tb-line)", color: "var(--tb-ink)" }}>
                  <span className="text-muted">Remark:</span> {safeText(user.remark)}
                </div>
              ) : null}

              <div className="row g-3 mb-1">
                <div className="col-6 col-md-4">
                  <div className="small text-muted">Email</div>
                  <div className="fw-semibold small">{safeText(user.email)}</div>
                </div>
                <div className="col-6 col-md-4">
                  <div className="small text-muted">Mobile</div>
                  <div className="fw-semibold small">{mobile}</div>
                </div>
                <div className="col-6 col-md-4">
                  <div className="small text-muted">Watchlist ID</div>
                  <div className="fw-semibold small">{safeText(item?.watchlist_id)}</div>
                </div>
              </div>

              <hr style={{ borderColor: "var(--tb-line)" }} />

              <DayBusinessSection
                title="Downline business"
                icon={<FaUsers />}
                rows={business.downline}
              />
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
}

export default function LeaderList() {
  const { logout } = useAuth();
  const [userId, setUserId] = useState("");
  const [remark, setRemark] = useState("");
  const [days, setDays] = useState(DEFAULT_DAYS);
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState({
    total: 0,
    days: DEFAULT_DAYS,
    max_days: DEFAULT_MAX_DAYS,
    from_date: "",
    to_date: "",
  });
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [selected, setSelected] = useState(null);

  const maxDays = n(meta.max_days) > 0 ? n(meta.max_days) : DEFAULT_MAX_DAYS;
  const dayOptions = useMemo(
    () => Array.from({ length: maxDays }, (_, i) => i + 1),
    [maxDays]
  );

  const token = () => localStorage.getItem("token");

  const fetchList = useCallback(
    async (daysValue = days) => {
      const safeDays = clampDays(daysValue, maxDays);
      if (Number(daysValue) > maxDays) {
        toast.error(`days cannot be higher than ${maxDays}`);
        setDays(maxDays);
      } else if (safeDays !== Number(daysValue)) {
        setDays(safeDays);
      }

      setLoading(true);
      try {
        const res = await axios.post(
          `${BASE_ENDPOINT}/admin/watchlist-list`,
          {
            token: token(),
            days: safeDays,
          },
          { headers: { "Content-Type": "application/json" } }
        );

        const payload = res?.data;
        if (payload?.error) {
          handleApiError(payload.error, logout);
          return;
        }

        const nextList = Array.isArray(payload?.data?.list) ? payload.data.list : [];
        setList(nextList);
        setMeta({
          total: payload?.data?.meta?.total ?? 0,
          days: payload?.data?.meta?.days ?? safeDays,
          max_days: payload?.data?.meta?.max_days ?? DEFAULT_MAX_DAYS,
          from_date: payload?.data?.meta?.from_date || "",
          to_date: payload?.data?.meta?.to_date || "",
        });

        setSelected((prev) => {
          if (!prev) return null;
          const id = prev?.user?.user_id;
          return nextList.find((x) => x?.user?.user_id === id) || null;
        });
      } catch (err) {
        console.log(err);
        toast.error("Failed to fetch leader list");
      } finally {
        setLoading(false);
      }
    },
    [days, logout, maxDays]
  );

  useEffect(() => {
    fetchList(DEFAULT_DAYS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onAdd = async (e) => {
    e.preventDefault();
    const id = String(userId || "").trim();
    if (!id) {
      toast.error("user_id is required");
      return;
    }
    if (!/^\d+$/.test(id)) {
      toast.error("user_id must be numeric");
      return;
    }

    setAdding(true);
    try {
      const res = await axios.post(
        `${BASE_ENDPOINT}/admin/watchlist-add`,
        {
          token: token(),
          user_id: Number(id),
          remark: String(remark || "").trim() || undefined,
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const payload = res?.data;
      if (payload?.error) {
        handleApiError(payload.error, logout);
        return;
      }

      toast.success(payload?.msg || "User added to watchlist");
      setUserId("");
      setRemark("");
      await fetchList(days);
    } catch (err) {
      console.log(err);
      toast.error("Failed to add user");
    } finally {
      setAdding(false);
    }
  };

  const onRemove = async (id) => {
    if (!id && id !== 0) return;
    const ok = window.confirm(`Remove user ${id} from leader list?`);
    if (!ok) return;

    setRemovingId(id);
    try {
      const res = await axios.post(
        `${BASE_ENDPOINT}/admin/watchlist-remove`,
        {
          token: token(),
          user_id: Number(id),
        },
        { headers: { "Content-Type": "application/json" } }
      );

      const payload = res?.data;
      if (payload?.error) {
        handleApiError(payload.error, logout);
        return;
      }

      toast.success(payload?.msg || "User removed from watchlist");
      if (selected?.user?.user_id === id) setSelected(null);
      await fetchList(days);
    } catch (err) {
      console.log(err);
      toast.error("Failed to remove user");
    } finally {
      setRemovingId(null);
    }
  };

  const onApplyDays = async () => {
    const safeDays = clampDays(days, maxDays);
    if (Number(days) > maxDays) {
      toast.error(`days cannot be higher than ${maxDays}`);
      setDays(maxDays);
      await fetchList(maxDays);
      return;
    }
    setDays(safeDays);
    await fetchList(safeDays);
  };

  const rangeText = useMemo(() => {
    if (!meta.from_date && !meta.to_date) return "—";
    return `${meta.from_date || "—"} → ${meta.to_date || "—"}`;
  }, [meta.from_date, meta.to_date]);

  return (
    <PageTransition>
      {/* Page header */}
      <div className="d-flex flex-wrap align-items-end justify-content-between gap-3 mb-3">
        <div>
          <h2 className="tb-page-title">Leader List</h2>
          <div className="tb-page-subtitle mb-0">
            {nfmt(meta.total)} leaders · {rangeText} · last {nfmt(meta.days)} day{n(meta.days) === 1 ? "" : "s"}
          </div>
        </div>

        <div className="d-flex align-items-end gap-2 flex-wrap">
          <div style={{ width: 120 }}>
            <label className="form-label small mb-1">Days</label>
            <select
              className="form-select form-select-sm"
              value={clampDays(days, maxDays)}
              onChange={(e) => setDays(clampDays(e.target.value, maxDays))}
              disabled={loading}
            >
              {dayOptions.map((d) => (
                <option key={d} value={d}>
                  {d} day{d > 1 ? "s" : ""}
                </option>
              ))}
            </select>
          </div>
          <button type="button" className="btn btn-outline-primary btn-sm" onClick={onApplyDays} disabled={loading}>
            Apply
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-2"
            onClick={() => fetchList(days)}
            disabled={loading}
          >
            <FaSyncAlt className={loading ? "tb-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Add bar */}
      <div className="card border-0 mb-3">
        <div className="card-body py-3">
          <form onSubmit={onAdd}>
            <div className="row g-2 align-items-end">
              <div className="col-12 col-md-3">
                <label className="form-label small mb-1">User ID</label>
                <input
                  className="form-control"
                  type="text"
                  inputMode="numeric"
                  placeholder="e.g. 102617"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  disabled={adding}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label small mb-1">Remark (optional)</label>
                <input
                  className="form-control"
                  type="text"
                  placeholder="Top leader"
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  disabled={adding}
                />
              </div>
              <div className="col-12 col-md-3">
                <button
                  type="submit"
                  className="btn btn-primary w-100 d-inline-flex align-items-center justify-content-center gap-2"
                  disabled={adding}
                >
                  <FaUserPlus />
                  {adding ? "Adding…" : "Add leader"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Users list */}
      <div className="card border-0 table-card">
        <div className="card-header d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <span className="tb-panel-icon">
              <FaCrown />
            </span>
            <span className="fw-semibold">Leaders</span>
          </div>
          <span className="badge text-bg-secondary">{nfmt(list.length)}</span>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>#</th>
                  <th>User</th>
                  <th>Mobile</th>
                  <th className="text-center">Status</th>
                  <th className="text-end">Team</th>
                  <th className="text-end">Coin</th>
                  <th className="text-end">USDT</th>
                  <th>Remark</th>
                  <th>Added</th>
                  <th className="text-center" style={{ width: 150 }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={`sk-${i}`}>
                      {Array.from({ length: 10 }).map((__, j) => (
                        <td key={j}>
                          <span className="tb-skeleton" style={{ width: "70%" }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : list.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-5">
                      <div
                        className="d-inline-flex align-items-center justify-content-center mb-2"
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: "var(--tb-radius)",
                          background: "var(--tb-accent-soft)",
                          color: "var(--tb-accent)",
                        }}
                      >
                        <FaCrown />
                      </div>
                      <div className="fw-semibold">No leaders yet</div>
                      <div className="text-muted small">Add a user ID above to build the watchlist.</div>
                    </td>
                  </tr>
                ) : (
                  list.map((item, index) => {
                    const user = item?.user || {};
                    const username = user.username;
                    const mobile =
                      user.mobile == null || user.mobile === ""
                        ? "—"
                        : `+${safeText(user.country_code)} ${safeText(user.mobile)}`;

                    return (
                      <tr key={item?.watchlist_id || user.user_id || index}>
                        <td className="text-muted small">{index + 1}</td>
                        <td>
                          <div className="fw-semibold">{safeText(user.name)}</div>
                          <div className="text-muted small">
                            ID {safeText(user.user_id)}
                          </div>
                        </td>
                        <td>{mobile}</td>
                        <td className="text-center">
                          <StatusBadge active={!!user.status} />
                        </td>
                        <td className="text-end">{nfmt(user.team_count)}</td>
                        <td className="text-end">
                          {money(user.balance?.coin?.total)}
                          <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                            Lock {money(user.balance?.coin?.lock)}
                          </div>
                        </td>
                        <td className="text-end">{money(user.balance?.usdt)}</td>
                        <td>
                          <span className="text-truncate d-inline-block" style={{ maxWidth: 160 }} title={safeText(user.remark)}>
                            {safeText(user.remark)}
                          </span>
                        </td>
                        <td className="small text-muted">{formatDate(user.added_date)}</td>
                        <td className="text-center">
                          <div className="d-inline-flex align-items-center gap-1">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                              onClick={() => setSelected(item)}
                            >
                              <FaInfoCircle /> Details
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              title="Remove"
                              disabled={removingId === user.user_id}
                              onClick={() => onRemove(user.user_id)}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <DetailsModal
        open={!!selected}
        item={selected}
        onClose={() => setSelected(null)}
        onRemove={onRemove}
        removing={removingId === selected?.user?.user_id}
      />
    </PageTransition>
  );
}
